import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isNil, unescape } from 'lodash';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import {
  LanguageDefaults,
  safelyExtractTextFromHTML,
  SupportedLanguages,
  VRCAudioBlobLocation,
  VRCAudioMetadata,
} from '@pwp-common';

import { SharedDirectiveModule } from '../../../../../directives/shared-directive.module';
import { CommunicationsService } from '../../../../../services/call/communications/communications.service';
import { OrgDataService } from '../../../../../services/orgs/org-data/org-data.service';
import { VRCAudioBlobService } from '../../../../../services/vrc-audio/vrc-audio-blob/vrc-audio-blob.service';
import { VRCAudioMetadataService } from '../../../../../services/vrc-audio/vrc-audio-metadata/vrc-audio-metadata.service';
import { FormFieldHelpComponent } from '../../../../form/form-field-help/form-field-help.component';
import { FormGroupControlValueAccessor } from '../../../../generic/abstract-classes/form-group-control-value-accessor';
import { makeAuditEntry } from '../../../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { AuditEntry } from '../../../../generic/settings/common/audit-entry/interfaces';
import { SettingsModule } from '../../../../generic/settings/settings.module';
import { AudioPlayerModule } from '../../../audio-player/audio-player.module';
import { createVRCAudioMetadata } from '../../helpers/create-vrc-audio-metadata/create-vrc-audio-metadata';
import { AudioFilesEditorOutput, SSMLAudio } from '../editor-output/audio-files-editor-output';
import { audioFilesEditorOutputToDb } from '../editor-output/audio-files-editor-output-to-db/audio-files-editor-output-to-db';
import { AudioFilesEditorFormType } from '../editor-output/audio-files-form-type';
import { dbToAudioFilesEditorOutput } from '../editor-output/db-to-audio-files-editor-output/db-to-audio-files-editor-output';
import { formToAudioFilesEditorOutput } from '../editor-output/form-to-audio-files-editor-output/form-to-audio-files-editor-output';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AudioFilesEditorComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => AudioFilesEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-audio-files-editor',
  standalone: true,
  imports: [
    AudioPlayerModule,
    ButtonModule,
    CommonModule,
    FileUploadModule,
    FormFieldHelpComponent,
    InputTextModule,
    InputTextareaModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    SettingsModule,
    SharedDirectiveModule,
    TabViewModule,
    ToastModule,
    TranslocoModule,
  ],
  templateUrl: './audio-files-editor.component.html',
  styleUrls: ['./audio-files-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR, MessageService],
})
export class AudioFilesEditorComponent
  extends FormGroupControlValueAccessor<AudioFilesEditorFormType, AudioFilesEditorOutput>
  implements OnInit
{
  ////////////////////////////////////////////////////////////////////////
  // Dependencies
  ////////////////////////////////////////////////////////////////////////

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly vrcAudioService = inject(VRCAudioMetadataService);

  private readonly vrcAudioBlobService = inject(VRCAudioBlobService);

  private readonly orgDataService = inject(OrgDataService);

  private readonly communicationsService = inject(CommunicationsService);

  private readonly messageService = inject(MessageService);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  public vrcAudioMetadata: VRCAudioMetadata | undefined;

  public audioUrl: string | undefined;

  public loading = true;

  public uploadInProgress = false;

  public currentLanguageCode = 'en';

  public errorGettingPlayURL = false;

  public auditEntry: AuditEntry | undefined;

  public availableLanguages: LanguageDefaults[] = [];

  public activeTabIndex = 0;

  public isNewRecord = false;

  private audioByLanguage: Record<string, SSMLAudio> = {};

  @ViewChild(FileUpload) public fileUploadComponent!: FileUpload;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Constructor
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  public override ngOnInit() {
    super.ngOnInit();

    const audioId = this.dialogConfig.data?.audioId;
    const availableLanguages = this.dialogConfig.data?.availableLanguages;

    if (audioId) {
      this.loadAudio(audioId, availableLanguages);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  public defineForm() {
    this.form = new UntypedFormGroup({
      displayName: new UntypedFormControl('', [Validators.required, Validators.maxLength(500)]),
      ssml: new UntypedFormControl('', [Validators.required, Validators.maxLength(4000)]),
      currentLanguage: new UntypedFormControl('en', [Validators.required]),
      selectedFile: new UntypedFormControl(undefined),
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////

  public override writeValue(value: AudioFilesEditorOutput) {
    if (isNil(value)) return;

    if (value.audioByLanguage) {
      this.audioByLanguage = { ...value.audioByLanguage };
    }

    const currentLanguageData = this.audioByLanguage[value.currentLanguage];

    this.form.patchValue(
      {
        displayName: value.displayName,
        currentLanguage: value.currentLanguage,
        ssml: currentLanguageData?.ssml ?? '',
        selectedFile: currentLanguageData?.selectedFile,
      },
      { emitEvent: false },
    );

    this.activeTabIndex = this.availableLanguages.findIndex((lang) => lang.getShortCode() === value.currentLanguage);
    if (this.activeTabIndex === -1) {
      this.activeTabIndex = 0;
    }

    if (this.vrcAudioMetadata) {
      const currentLanguage = this.currentLanguage.value;
      this.currentLanguageCode = currentLanguage;

      if (currentLanguageData?.selectedFile) {
        this.ssml.disable();
      } else {
        this.ssml.enable();
      }

      if (!this.isNewRecord) {
        this.loadAudioUrl(currentLanguage).catch((error) => {
          console.error('Error loading audio URL:', error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        });
      } else {
        this.audioUrl = undefined;
        this.errorGettingPlayURL = false;
        this.loading = false;
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////

  public parseValueChange(value: AudioFilesEditorFormType): AudioFilesEditorOutput {
    this.audioByLanguage[value.currentLanguage] = {
      ssml: value.ssml ?? '',
      selectedFile: value.selectedFile,
    };

    return formToAudioFilesEditorOutput({ formType: value, audioByLanguage: this.audioByLanguage });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  protected makeValidationErrors() {
    return {
      'audio-files-editor': this.form.value,
    };
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Private Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  private loadAudio(audioId: string, availableLanguages: string[]) {
    this.loading = true;
    this.changeDetectorRef.detectChanges();

    this.vrcAudioService
      .getDoc(audioId)
      .pipe(untilDestroyed(this))
      .subscribe((vrcAudio) => {
        this.auditEntry = makeAuditEntry(vrcAudio);

        this.setAvailableLanguages(availableLanguages);

        const hasCurrentLanguage = availableLanguages.includes(this.currentLanguageCode);
        if (!hasCurrentLanguage && this.availableLanguages.length > 0) {
          this.currentLanguageCode = this.availableLanguages[0].getShortCode();
        }

        if (vrcAudio) {
          this.vrcAudioMetadata = vrcAudio;
          this.isNewRecord = false;
        } else {
          this.vrcAudioMetadata = createVRCAudioMetadata({ audioId });
          this.isNewRecord = true;
        }

        const editorOutput = dbToAudioFilesEditorOutput({
          vrcAudioMetadata: this.vrcAudioMetadata,
          currentLanguage: this.currentLanguageCode,
          availableLanguages,
        });

        this.writeValue(editorOutput);
      });
  }

  private setAvailableLanguages(languageCodes: string[]) {
    this.availableLanguages = [];

    if (languageCodes.length === 0) return;

    for (const languageCode of languageCodes) {
      const languageDefaults = SupportedLanguages.getDefaults(languageCode);
      this.availableLanguages.push(languageDefaults);
    }

    this.availableLanguages.sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Public Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  public onLanguageTabChange(event: { index: number }) {
    if (this.availableLanguages.length === 0) return;

    const selectedLanguage = this.availableLanguages[event.index];
    if (selectedLanguage) {
      const newLanguageCode = selectedLanguage.getShortCode();
      const currentLanguageCode = this.currentLanguage.value;

      if (newLanguageCode !== currentLanguageCode) {
        this.audioByLanguage[currentLanguageCode] = {
          ssml: this.ssml.value ?? '',
          selectedFile: this.selectedFile.value,
        };

        this.currentLanguageCode = newLanguageCode;

        this.loading = true;
        this.errorGettingPlayURL = false;

        this.audioUrl = undefined;
        this.changeDetectorRef.detectChanges();

        this.clearFileUploadComponent();

        this.restoreLanguageState(newLanguageCode);

        if (!this.isNewRecord) {
          this.loadAudioUrl(newLanguageCode)
            .catch((error) => {
              if (this.currentLanguageCode === newLanguageCode) {
                console.error('Error loading audio URL for language:', error);
                this.errorGettingPlayURL = true;
              }
            })
            .finally(() => {
              if (this.currentLanguageCode === newLanguageCode) {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              }
            });
        } else {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      }
    }
  }

  private async loadAudioUrl(languageCode: string) {
    if (!this.vrcAudioMetadata) {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const audioMap = this.vrcAudioMetadata.getAudio();
    const currentAudio = audioMap.get(languageCode);

    if (!currentAudio) {
      this.audioUrl = undefined;
      this.errorGettingPlayURL = true;
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const existingAudioUrl = currentAudio.getAudioURL();
    if (!existingAudioUrl || existingAudioUrl.trim() === '') {
      this.audioUrl = undefined;
      this.errorGettingPlayURL = true;
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    /**
     * IMPORTANT: Use the URL stored in the database directly. DO NOT generate a new download URL.
     * There is a long-standing production bug where some audio files were uploaded
     * to the wrong directory. However, the download URLs stored in the database are correct.
     *
     * In the backend, we escape URLs, meaning we convert '&' to the equivalent HTML entity '&amp;'.
     * We need to unescape them before using them on the frontend; otherwise, they won't work.
     */
    this.audioUrl = unescape(existingAudioUrl);
    this.errorGettingPlayURL = false;
    this.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  public upload = async () => {
    if (!this.isValid() || !this.vrcAudioMetadata) return;

    this.dialogConfig.closable = false;
    this.uploadInProgress = true;
    this.changeDetectorRef.detectChanges();

    try {
      const orgData = await firstValueFrom(this.orgDataService.getOrgData());
      const orgId = orgData.getId();
      const audioId = this.vrcAudioMetadata.getId();

      const audioURLsByLanguage = new Map<string, string>();

      for (const [languageCode, audioData] of Object.entries(this.audioByLanguage)) {
        const languageDefaults = SupportedLanguages.getDefaults(languageCode);

        if (audioData.selectedFile) {
          const fileFullPath = new VRCAudioBlobLocation().getFileFullPath(orgId, audioId, languageDefaults);

          const uploadTask = this.vrcAudioBlobService.upload(fileFullPath, audioData.selectedFile);

          // eslint-disable-next-line no-await-in-loop
          await lastValueFrom(uploadTask.snapshotChanges());

          // eslint-disable-next-line no-await-in-loop
          const audioURL = await firstValueFrom(this.vrcAudioBlobService.getDownloadLink(fileFullPath, false));
          audioURLsByLanguage.set(languageCode, audioURL);
        }
      }

      this.vrcAudioMetadata = audioFilesEditorOutputToDb({
        originalVrcAudioMetadata: cloneDeep(this.vrcAudioMetadata),
        audioFilesEditorOutput: {
          displayName: this.displayName.value,
          currentLanguage: this.currentLanguage.value,
          audioByLanguage: { ...this.audioByLanguage },
        },
        audioURLsByLanguage,
      });

      if (this.isNewRecord) {
        await firstValueFrom(
          this.vrcAudioService.upload({
            obj: this.vrcAudioMetadata,
            mode: 'create',
          }),
        );
      } else {
        await this.vrcAudioService.uploadIfNotEditingStaleObject(this.vrcAudioMetadata);
      }

      const response = await this.communicationsService.onVrcAudioUpdate(this.vrcAudioMetadata.getId());
      if (response?.getError()) {
        this.messageService.add({
          key: 'audio-files-editor',
          severity: 'error',
          summary: translate('audio-files-editor.uploadAudioErrorTitle'),
          detail: translate('audio-files-editor.uploadAudioErrorDetail'),
        });
        throw new Error();
      }

      this.isNewRecord = false;

      await this.refreshData();
    } finally {
      this.dialogConfig.closable = true;
      this.uploadInProgress = false;
      this.changeDetectorRef.detectChanges();
    }
  };

  public isValid = (): boolean => {
    if (!this.displayName.valid) {
      return false;
    }

    const currentLanguage = this.currentLanguage.value;
    this.audioByLanguage[currentLanguage] = {
      ssml: this.ssml.value ?? '',
      selectedFile: this.selectedFile.value,
    };

    const requiredLanguageCode = this.getRequiredLanguageCode();
    let requiredLanguageHasContent = false;

    for (const [languageCode, audioData] of Object.entries(this.audioByLanguage)) {
      const hasSSML = !isNil(audioData.ssml) && audioData.ssml.trim().length > 0;
      const hasSelectedFile = !isNil(audioData.selectedFile);
      const hasUploadedAudioFile = this.hasUploadedAudioFile(languageCode);

      if (hasSSML && hasSelectedFile) {
        return false;
      }

      if (hasSSML && audioData.ssml.length > 4000) {
        return false;
      }

      if (languageCode === requiredLanguageCode && (hasSSML || hasSelectedFile || hasUploadedAudioFile)) {
        requiredLanguageHasContent = true;
      }
    }

    return requiredLanguageHasContent;
  };

  private getRequiredLanguageCode(): string {
    const englishCode = SupportedLanguages.en.getShortCode();

    const hasEnglish = this.availableLanguages.some((language) => language.getShortCode() === englishCode);
    if (hasEnglish) {
      return englishCode;
    }

    if (this.availableLanguages.length > 0) {
      return this.availableLanguages[0].getShortCode();
    }

    return englishCode;
  }

  /**
   * Returns true if an audio file has been uploaded for the given language.
   */
  private hasUploadedAudioFile(languageCode: string): boolean {
    if (isNil(this.vrcAudioMetadata)) {
      return false;
    }

    const audioMap = this.vrcAudioMetadata.getAudio();
    const audio = audioMap.get(languageCode);

    if (!audio) {
      return false;
    }

    const ssml = audio.getSSML();
    if (!isNil(ssml) && ssml.trim().length > 0) {
      return false;
    }

    const audioUrl = audio.getAudioURL();
    return !isNil(audioUrl) && audioUrl.trim().length > 0;
  }

  public refreshData = async (): Promise<void> => {
    const audioId = this.dialogConfig.data?.audioId;
    const availableLanguages = this.dialogConfig.data?.availableLanguages ?? [];
    if (audioId) {
      this.loadAudio(audioId, availableLanguages);
    }
  };

  public resetForm = (): void => {
    this.clearSelectedFile();

    if (this.vrcAudioMetadata) {
      const currentLanguage = this.currentLanguage.value;
      this.loadSSML(currentLanguage);

      this.form.patchValue({
        displayName: this.vrcAudioMetadata.getDisplayName(),
      });
    }
  };

  public onFileSelect(event: { originalEvent: Event; files: FileList }) {
    const { files } = event;
    const [selectedFile] = Array.from(files ?? []);
    if (selectedFile) {
      this.form.patchValue({
        selectedFile,
        ssml: '',
      });

      const currentLanguage = this.currentLanguage.value;
      this.audioByLanguage[currentLanguage] = {
        ssml: '',
        selectedFile,
      };

      this.ssml.disable();
      this.changeDetectorRef.detectChanges();
    }
  }

  public clearSelectedFile(): void {
    this.clearFileUploadComponent();

    const currentLanguage = this.currentLanguage.value;

    this.form.patchValue({
      selectedFile: undefined,
    });

    this.audioByLanguage[currentLanguage] = {
      ssml: this.ssml.value,
      selectedFile: undefined,
    };

    this.ssml.enable();
    this.changeDetectorRef.detectChanges();
  }

  private clearFileUploadComponent(): void {
    if (this.fileUploadComponent) {
      this.fileUploadComponent.clear();
    }
  }

  private restoreLanguageState(languageCode: string): void {
    const savedState = this.audioByLanguage[languageCode];

    if (!savedState) {
      this.form.patchValue({ currentLanguage: languageCode });
      this.loadSSML(languageCode);
      this.ssml.enable();
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.form.patchValue({
      currentLanguage: languageCode,
      ssml: savedState.ssml,
      selectedFile: savedState.selectedFile,
    });

    if (savedState.selectedFile) {
      this.ssml.disable();
    } else {
      this.ssml.enable();
    }

    this.changeDetectorRef.detectChanges();
  }

  private loadSSML(languageCode: string): void {
    if (!this.vrcAudioMetadata) return;

    const audioMap = this.vrcAudioMetadata.getAudio();
    const audio = audioMap.get(languageCode);
    const strippedSSML = safelyExtractTextFromHTML(audio?.getSSML());

    this.form.patchValue({ ssml: strippedSSML });

    this.audioByLanguage[languageCode] = {
      ssml: strippedSSML,
      selectedFile: undefined,
    };
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get hasAudio() {
    return !!this.audioUrl && !this.errorGettingPlayURL;
  }

  public get hasSSMLContent() {
    const ssmlValue = this.ssml.value;
    return !!(ssmlValue && ssmlValue.trim());
  }

  public get hasSelectedFile() {
    const selectedFile = this.selectedFile.value;
    return !!selectedFile;
  }

  public get isFileUploadDisabled() {
    return this.hasSSMLContent;
  }

  public getCurrentLanguageDisplayName() {
    const currentLanguage = this.currentLanguage.value;
    const language = this.availableLanguages.find((lang) => lang.getShortCode() === currentLanguage);
    return language?.getDisplayName();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Form Getters
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get displayName() {
    return this.form.get('displayName') as UntypedFormControl;
  }

  public get ssml() {
    return this.form.get('ssml') as UntypedFormControl;
  }

  public get selectedFile() {
    return this.form.get('selectedFile') as UntypedFormControl;
  }

  public get currentLanguage() {
    return this.form.get('currentLanguage') as UntypedFormControl;
  }
}
